/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { TContentRating } from "../types";

/**
 * Read an ID field that may arrive as a string or a number.
 * @param entry - Source object
 * @param key - Field name
 * @returns The ID as a trimmed string, or an empty string
 */
function readId(entry: Record<string, unknown>, key: string): string {
  const value: unknown = entry[key];
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  return "";
}

/**
 * Convert a rating list from any Web payload into {@link TContentRating}s.
 *
 * Accepts both shapes in use: `{ ratingTypeId, ratingValueId }` (docs,
 * `parentalSettings-ratingLimits`) and `{ typeId, valueId }` (the
 * `internalRatingList` on Web content payloads). Malformed entries are
 * dropped; callers treat a resulting empty list as unrated.
 *
 * @param list - Raw rating list
 * @returns Valid rating pairs
 */
export function toContentRatings(list: unknown): Array<TContentRating> {
  if (!Array.isArray(list)) {
    return [];
  }
  const ratings: Array<TContentRating> = [];
  for (const item of list as Array<unknown>) {
    if (item === null || typeof item !== "object") {
      continue;
    }
    const entry: Record<string, unknown> = item as Record<string, unknown>;
    const ratingTypeId: string =
      readId(entry, "ratingTypeId") || readId(entry, "typeId");
    const ratingValueId: string =
      readId(entry, "ratingValueId") || readId(entry, "valueId");
    if (ratingTypeId !== "" && ratingValueId !== "") {
      ratings.push({ ratingTypeId, ratingValueId });
    }
  }
  return ratings;
}
